import Button from '@hotmart/space-lib/dist/components/button'
import CustomAlert from '@hotmart/space-lib/dist/components/customAlert'
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket'
import { faCircleXmark } from '@fortawesome/pro-light-svg-icons/faCircleXmark'
import { useDropzone } from 'react-dropzone'
import { useState } from 'react'
import { storage } from 'src/utils/storage'
import Icon from '../Icon'
import Loading from '../Loading'
import { LabelWrapper, LoadingWrapper } from './dropZoneUploader.styles'
import {
  AlertError,
  DropZoneResults,
  DropZoneUploaderProps,
  UploadResult
} from './dropZoneUploader.types'
import {
  filesWithOriginalType,
  getImageDimensions,
  normalizeMediaKey
} from './dropZoneUploader.utils'

const DropZoneUploader = ({
  buttonLabel,
  text,
  disclaimer,
  maxFiles = 1,
  maxSize,
  acceptedTypes,
  errorMessages,
  className,
  onLoading,
  onSuccess,
  onError,
  children
}: DropZoneUploaderProps) => {
  const [alertError, setAlertError] = useState<AlertError>()
  const [loading, setLoading] = useState(false)

  const isValidFileType = (files: File[]) => {
    if (!acceptedTypes) {
      return true
    }

    const validTypes = Object.keys(acceptedTypes)

    return files.some((file) => validTypes.includes(file.type))
  }

  const isValidFileSize = (files: File[]) => {
    if (!maxSize) {
      return true
    }

    return files.some((file) => file.size <= maxSize)
  }

  const isValidFile = (files: File[]) => {
    setAlertError({
      show: false
    })

    try {
      if (!isValidFileType(files)) {
        onError?.(errorMessages?.invalidFileType)

        return setAlertError({
          show: true,
          message: errorMessages?.invalidFileType
        })
      }

      if (!isValidFileSize(files)) {
        onError?.(errorMessages?.invalidFileSize)

        return setAlertError({
          show: true,
          message: errorMessages?.invalidFileSize
        })
      }

      return true
    } catch {
      setAlertError({
        show: false
      })
    }
  }

  const processFiles = async (files: File[]): Promise<DropZoneResults> => {
    const uploadPromises = files.map((file) => storage.upload(file))
    const results = await Promise.allSettled(uploadPromises)
    const successfulUploads: UploadResult[] = []
    const failedUploads: unknown[] = []

    for (const [index, result] of results.entries()) {
      if (result.status === 'fulfilled') {
        const blobUrl = URL.createObjectURL(files[index])
        const targetFile = files[index]
        const isImage = targetFile.type.includes('image')
        const dimensions = isImage ? await getImageDimensions(blobUrl) : undefined
        const mediaKey = normalizeMediaKey(result.value)

        successfulUploads.push({
          file: {
            name: targetFile.name,
            size: targetFile.size,
            type: targetFile.type,
            dimensions
          },
          mediaKey,
          blobUrl
        })
      }

      if (result.status === 'rejected') {
        failedUploads.push(result.reason)
      }
    }

    return {
      failedUploads,
      successfulUploads
    }
  }

  const onDrop = async (files: File[]) => {
    const filesWithTypes = await filesWithOriginalType(files)

    if (!isValidFile(filesWithTypes)) {
      return
    }

    setLoading(true)
    onLoading?.(true)

    try {
      const { successfulUploads, failedUploads } = await processFiles(files)

      if (failedUploads.length) {
        onError?.(failedUploads)
      }

      if (successfulUploads.length) {
        onSuccess(successfulUploads)
      }
    } catch (error) {
      onError?.(error)
    } finally {
      setLoading(false)
      onLoading?.(false)
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    accept: acceptedTypes,
    onDrop
  })

  if (loading) {
    return (
      <LoadingWrapper>
        <Loading full />
      </LoadingWrapper>
    )
  }

  return (
    <div data-test="drop-zone-uploader" className={className}>
      <LabelWrapper
        htmlFor="upload"
        className="_bg-gray-100 _rounded _d-flex _w-full _justify-content-center _align-items-center _cursor-pointer _mb-1 _position-relative"
        {...getRootProps()}
      >
        <input {...getInputProps()} data-test="drop-zone-uploader-input" />

        <div className="_d-flex _flex-column _align-items-center">
          <Button variation="secondary">
            <Icon icon={faArrowUpFromBracket} />
            <span className="_ml-3">{buttonLabel}</span>
          </Button>

          <span className="_mt-4">{text}</span>
        </div>

        {children}
      </LabelWrapper>

      {disclaimer && <span className="_text-gray-500 _text-1">{disclaimer}</span>}

      {alertError?.show && (
        <CustomAlert
          variation="danger"
          icon={faCircleXmark}
          iconClassName="_text-4"
          className="_mt-3 _mb-0"
        >
          {alertError.message}
        </CustomAlert>
      )}
    </div>
  )
}

export default DropZoneUploader
